import { ApolloClient, InMemoryCache, HttpLink, ApolloLink } from '@apollo/client';
import { SetContextLink } from '@apollo/client/link/context';
import type { AuthenticationFlow } from '@heidelbergmaterials/fcl-authclient';
import { ENV } from '../settings';

export function createApolloClient(authFlow: AuthenticationFlow) {
  const graphQlPath = import.meta.env.DEV ? '/graphql' : '/api/graphql';

  const httpLink = new HttpLink({
    // AFD strips /api before forwarding to AKS. Local Vite development bypasses
    // AFD and calls the backend's internal /graphql route directly.
    uri: `${ENV.api_base_url.replace(/\/+$/, '')}${graphQlPath}`,
  });

  const idempotencyLink = new ApolloLink((operation, forward) => {
    const { idempotencyKey } = operation.getContext();

    if (idempotencyKey) {
      operation.setContext(({ headers = {} }) => ({
        headers: {
          ...headers,
          'Idempotency-Key': idempotencyKey as string,
        },
      }));
    }

    return forward(operation);
  });

  const authLink = new SetContextLink((prevContext) => {
    const token = authFlow.getAccessToken();
    return {
      ...prevContext,
      headers: {
        ...prevContext.headers,
        authorization: token ? `Bearer ${token}` : '',
      },
    };
  });

  return new ApolloClient({
    link: ApolloLink.from([authLink, idempotencyLink, httpLink]),
    cache: new InMemoryCache({
      typePolicies: {
        // FilterOption appears in several sibling lists (areas, countries,
        // brands, ...) that reuse the same numeric ids. Normalising by
        // __typename:id would collide them into one cache entry (last write
        // wins), so e.g. the Country dropdown would show Brand values.
        // These options are always fetched inside their parent and never
        // referenced on their own, so store them inline (no normalisation).
        FilterOption: { keyFields: false },
      },
    }),
  });
}
